import { useEffect, useState } from 'react'
import { Head, useForm } from '@inertiajs/react'
import { Card } from '../../components/core/Card.jsx'
import { Button } from '../../components/core/Button.jsx'
import { StatusDot } from '../../components/core/StatusDot.jsx'
import { Field } from '../../components/forms/Field.jsx'
import { Input } from '../../components/forms/Input.jsx'
import { Textarea } from '../../components/forms/Textarea.jsx'
import { Select } from '../../components/forms/Select.jsx'
import { Radio } from '../../components/forms/Radio.jsx'
import SignedInLayout from '../../signed_in/SignedInLayout.jsx'
import { IndustryMatchCard, stillAsking } from '../../shared/IndustryMatch.jsx'

// The live city check is in-field feedback only. BrandSetup re-resolves on submit
// and that is what decides, so this indicator may lag or be briefly wrong.
// county_choices arrives when a city and state name a place in two or more
// counties; setup refuses to guess, so the choice is required before continuing.

// `stop` and `stop_sentence` come from `Billing::Summary`, which asks the same
// three questions `Collection::FirstRun` asks before it spends — so the line under
// the button describes the dispatch this button triggers rather than a wish.
//
// **`stop` defaults to 'none', not null, and the asymmetry with `/billing` is the
// point.** There, a null stop means "draw no banner" and a missing prop costs
// nothing. Here, a null stop means "promise a collection", so a forgotten prop
// would reprint the exact false sentence this page is being fixed for. 'none' is a
// real member of `Billing::Summary::STOPS` and carries no sentence, so the
// fail-safe reads as nothing at all.
function BrandsNewForm({
  trades = [],
  regions = [],
  first = true,
  county_choices = [],
  industry_match = null,
  stop = 'none',
  stop_sentence = null,
  errors = {},
}) {
  // The keys BrandsController permits. `website` and `trade` are the names a
  // person would pick and neither is read — `params.permit` drops an unpermitted
  // key and `create` then succeeds having changed nothing, except here, where
  // `BrandSetup` takes `vertical:` as a required keyword and the dropped param
  // raised. That was a 500 on the one screen every signup must pass through.
  const form = useForm({
    name: '',
    domain: '',
    vertical: '',
    industry: '',
    // The answer "keep it" to "Did you mean …?", so the same words are not
    // matched a second time. Cleared whenever the words change.
    industry_unlisted: false,
    city: '',
    region: '',
    county: '',
    aliases: '',
  })
  const check = useCityCheck(form.data.city, form.data.region)
  const ambiguous = county_choices.length > 0
  // "Something else": the questions are written next and read before anything
  // is collected, so the button and the sentence beside it change to match.
  const custom = form.data.vertical === 'other'
  const asking = custom && !form.data.industry_unlisted && stillAsking(industry_match, form.data.industry)

  // "Use Pest control" switches the industry and **does not submit**: a listed
  // industry starts collecting when the form is sent, and this button was
  // pressed by somebody expecting to read questions first. So the form changes
  // under them and the main button, now "Start measuring", is theirs to press.
  function chooseListed() {
    form.setData((d) => ({ ...d, vertical: industry_match.key, industry: '', industry_unlisted: false }))
  }

  function keepOwn() {
    form.setData('industry_unlisted', true)
    form.post('/brands')
  }

  function submit(e) {
    e.preventDefault()
    form.post('/brands')
  }

  return (
    <>
      <Head title={first ? 'Add your business' : 'Add a business'} />
      <main className="mx-auto min-h-screen w-full max-w-2xl px-6 py-12">
        <header className="mb-8 flex flex-col gap-2">
          <h1 className="page-title">
            {first ? 'Add your business' : 'Add a business'}
          </h1>
          <p className="max-w-[62ch] text-[15px] text-[color:var(--text-body)]">
            {first
              ? 'This is the last step before anything is measured. The questions each AI assistant is asked are determined by the industry and the city.'
              : 'A business is tracked on its own questions, in its own city, and counts against your plan separately.'}
          </p>
        </header>

        <form onSubmit={submit} className="flex flex-col gap-6">
          <Card title="The business" description="What the answers are matched against">
            <div className="flex flex-col gap-5">
              <Field
                label="Business name"
                htmlFor="name"
                required
                hint="Spelled the way customers write it."
                error={errors.name}
              >
                <Input
                  id="name"
                  name="name"
                  placeholder="e.g. Cumberland Comfort Systems"
                  value={form.data.name}
                  invalid={Boolean(errors.name)}
                  onChange={(e) => form.setData('name', e.target.value)}
                />
              </Field>

              {/*
                Optional, and the field exists because normalisation can only
                reach *mechanical* variants of one name — suffix, punctuation,
                casing, spacing. A trading name, a former name or an abbreviation
                is a different name, and no rule derives it. `brands.aliases` was
                created for this on 2026-08-17 and had no writer until now.

                A textarea rather than repeatable inputs: the wire format stays a
                single scalar, and `params.permit(:aliases)` drops an array
                silently — a 200 that saves nothing, which is how three batch-2
                forms failed.
              */}
              <Field
                label="Other names for this business"
                htmlFor="aliases"
                hint="Optional, one per line. A trading name, a former name, or an abbreviation customers use. Spelling, punctuation and spacing are already handled."
                error={errors.aliases}
              >
                <Textarea
                  id="aliases"
                  name="aliases"
                  rows={3}
                  placeholder={'e.g. Cumberland Comfort\nCCS Heating & Air'}
                  value={form.data.aliases}
                  invalid={Boolean(errors.aliases)}
                  onChange={(e) => form.setData('aliases', e.target.value)}
                />
              </Field>

              <Field
                label="Website"
                htmlFor="domain"
                hint="Optional. Used to tell whether your own pages are cited as sources."
                error={errors.domain}
              >
                <Input
                  id="domain"
                  name="domain"
                  type="text"
                  inputMode="url"
                  iconLeft="globe"
                  placeholder="e.g. cumberlandcomfort.com"
                  value={form.data.domain}
                  invalid={Boolean(errors.domain)}
                  onChange={(e) => form.setData('domain', e.target.value)}
                />
              </Field>

              {/*
                The hint read "Chosen, not read off the website — the question
                set depends on it." Tone rule 3: it argues against inferring the
                trade from a domain, which is a choice the reader never proposed
                and a design decision of ours. Internal reasoning goes in the
                repo, the way cost and security reasoning already do. The
                grader's form states the same field forwards and this matches it
                now.
              */}
              <Field
                label="Industry"
                htmlFor="vertical"
                required
                hint="Decides which twenty questions we ask."
                error={errors.vertical}
              >
                <Select
                  id="vertical"
                  name="vertical"
                  value={form.data.vertical}
                  invalid={Boolean(errors.vertical)}
                  onChange={(e) => form.setData('vertical', e.target.value)}
                >
                  <option value="">Choose an industry</option>
                  {trades.map((t) => (
                    <option key={valueOf(t)} value={valueOf(t)}>
                      {labelOf(t)}
                    </option>
                  ))}
                  {/* `BrandSetup::OTHER`. Last, so the curated seven — whose
                      questions are hand-written and benchmarked — are what a
                      reader meets first. */}
                  <option value="other">Something else</option>
                </Select>
              </Field>

              {/*
                **A business outside the curated seven**, decided 2026-10-12. Its
                questions are generated from these words and shown in full
                before anything is collected; they cannot be edited, so the words
                are the customer's lever. The text stays theirs — it is shown on
                their screens and in nobody else's dropdown.
              */}
              {custom && (
                <Field
                  label="What kind of business is it?"
                  htmlFor="industry"
                  required
                  hint="A few words, the way a customer would say it. For example: dog grooming."
                  error={errors.industry}
                >
                  <Input
                    id="industry"
                    name="industry"
                    maxLength={60}
                    value={form.data.industry}
                    invalid={Boolean(errors.industry)}
                    onChange={(e) => {
                      const industry = e.target.value
                      form.setData((d) => ({ ...d, industry, industry_unlisted: false }))
                    }}
                  />
                </Field>
              )}
            </div>
          </Card>

          {asking && (
            <IndustryMatchCard
              match={industry_match}
              onUse={chooseListed}
              onKeep={keepOwn}
              processing={form.processing}
            />
          )}

          <Card title="The market" description="Where the searches are run from">
            <div className="flex flex-col gap-5">
              <Field
                label="City"
                htmlFor="city"
                required
                hint="The city customers search in. Searches are run from there, so a different city is a different market."
                error={errors.city}
              >
                <Input
                  id="city"
                  name="city"
                  iconLeft="map-pin"
                  placeholder="e.g. Nashville"
                  value={form.data.city}
                  invalid={Boolean(errors.city) || check.status === 'unknown'}
                  onChange={(e) => form.setData('city', e.target.value)}
                />
              </Field>

              <Field label="State" htmlFor="region" required error={errors.region}>
                <Select
                  id="region"
                  name="region"
                  value={form.data.region}
                  invalid={Boolean(errors.region)}
                  onChange={(e) => form.setData('region', e.target.value)}
                >
                  <option value="">Choose a state</option>
                  {regions.map((r) => (
                    <option key={valueOf(r)} value={valueOf(r)}>
                      {labelOf(r)}
                    </option>
                  ))}
                </Select>
              </Field>

              <CityIndicator check={check} />

              <p className="text-[13.5px] text-[color:var(--text-muted)]">
                Cities in the United States only.
              </p>
            </div>
          </Card>

          {ambiguous && (
            <Card variant="signal" title="Which county?">
              <div className="flex flex-col gap-4">
                <p className="max-w-[62ch] text-[13.5px] text-[color:var(--text-body)]">
                  There is more than one {form.data.city} in {regionLabel(regions, form.data.region)}.
                  Choose the county this business is in.
                </p>
                <div className="flex flex-col gap-2">
                  {county_choices.map((c) => (
                    <Radio
                      key={valueOf(c)}
                      name="county"
                      value={valueOf(c)}
                      label={labelOf(c)}
                      checked={form.data.county === valueOf(c)}
                      onChange={(e) => form.setData('county', e.target.value)}
                    />
                  ))}
                </div>
                {errors.county && (
                  <p className="text-[13.5px] text-[color:var(--danger)]">{errors.county}</p>
                )}
                <p className="max-w-[62ch] text-[13.5px] text-[color:var(--text-muted)]">
                  The answer is stored with the questions, so later runs keep it.
                </p>
              </div>
            </Card>
          )}

          <div className="flex items-center gap-4">
            <Button
              variant="primary"
              size="lg"
              as="button"
              loading={form.processing}
              disabled={(ambiguous && !form.data.county) || asking}
            >
              {custom ? 'Write the questions' : first ? 'Start measuring' : 'Add this business'}
            </Button>
            {/* For "Something else", nothing is collected on submit — the
                questions come back to be read first — so the sentence promising
                a collection would be false whatever `stop` says. */}
            <span className="max-w-[46ch] text-[13.5px] text-[color:var(--text-muted)]">
              {custom
                ? 'You read all twenty before anything is collected.'
                : stop === null
                  ? 'The first collection starts straight away.'
                  : stop_sentence}
            </span>
          </div>
        </form>
      </main>
    </>
  )
}

function CityIndicator({ check }) {
  if (check.status === 'idle') return null
  if (check.status === 'checking') {
    return (
      <span className="flex items-center gap-2 text-[13.5px] text-[color:var(--text-muted)]">
        <StatusDot state="data" pulse label="Checking that city" />
      </span>
    )
  }
  if (check.status === 'confirmed') {
    return (
      <span className="flex items-center gap-2 text-[13.5px] text-[color:var(--text-body)]">
        <StatusDot state="visible" label={check.label} />
      </span>
    )
  }
  if (check.status === 'ambiguous') {
    return (
      <span className="flex items-center gap-2 text-[13.5px] text-[color:var(--text-body)]">
        <StatusDot state="data" label="More than one place has this name" />
      </span>
    )
  }
  if (check.status === 'unknown') {
    return (
      <span className="flex items-center gap-2 text-[13.5px] text-[color:var(--text-body)]">
        <StatusDot state="unknown" />
        We could not find that city. Try a different spelling, or the nearest larger town.
      </span>
    )
  }
  // The fall-through is `unchecked`, which exists because the check **fails
  // open**: an unseeded `serp_locations` table must not turn the signup form
  // into one that rejects every city in America. This line read "The city list
  // is unavailable, so nothing was confirmed. You can continue." — tone rule 1,
  // with an absence as the subject, and it left the reader guessing what
  // happens to what they typed. It goes in as typed, which is the fact worth
  // stating.
  return (
    <span className="flex items-center gap-2 text-[13.5px] text-[color:var(--text-muted)]">
      <StatusDot state="unknown" />
      The city list is unavailable just now, so this one goes in as you typed it. You can continue.
    </span>
  )
}

function useCityCheck(city, region) {
  const [check, setCheck] = useState({ status: 'idle', label: null })

  useEffect(() => {
    if (!city || city.trim().length < 3 || !region) {
      setCheck({ status: 'idle', label: null })
      return
    }
    let live = true
    setCheck({ status: 'checking', label: null })
    const timer = setTimeout(() => {
      const query = `city=${encodeURIComponent(city)}&region=${encodeURIComponent(region)}`
      fetch(`/locations/resolve?${query}`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then((body) => live && setCheck({ status: body.status, label: body.label }))
        .catch(() => live && setCheck({ status: 'unchecked', label: null }))
    }, 350)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [city, region])

  return check
}

const valueOf = (o) => (typeof o === 'string' ? o : (o.value ?? o.name))
const labelOf = (o) => (typeof o === 'string' ? o : o.label)
const regionLabel = (regions, value) => {
  const hit = regions.find((r) => valueOf(r) === value)
  return hit ? labelOf(hit) : 'that state'
}


// **The signed-in header, in its before-a-business form for the first.**
// `/dashboard` redirects here while an account has no business, so the first
// one's header leaves out Overview and Questions, which would loop back to this
// page — but keeps Plan, Account and sign-out. Until 2026-10-27 (Next Steps
// #20) the first was bare, and a new customer had no way to any of the three. A
// second business is reached by somebody who already has a dashboard, and gets
// the whole header.
//
// **A wrapper, not a conditional `BrandsNew.layout` function**, and that was
// tried first. Inertia 3 calls a function layout twice — once with the page's
// *props* to see whether it returns an element, then again with the page
// element — so a layout that reads `page.props.first` meets a props object on
// the first call and throws, rendering a blank page. `withSignedInLayout` works
// only because it never looks at its argument. A component has no such contract.
//
// Compared with `false`, so a missing prop falls back to the first-business
// header that `first = true` above describes.
export default function BrandsNew(props) {
  return (
    <SignedInLayout beforeBusiness={props.first !== false}>
      <BrandsNewForm {...props} />
    </SignedInLayout>
  )
}
